import {
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  updateDoc,
  addDoc,
} from '../firebase.config';
import { arrayUnion } from 'firebase/firestore';
import { getCollectionForCompany } from './dataAdapter';
import { ENABLE_WORK_ORDERS } from '../config/featureFlags';

export const WORK_ORDER_STATUSES = ['draft', 'active', 'completed', 'cancelled'];
export const PHASE_STATUSES = ['pending', 'in_progress', 'completed'];

export const LEDGER_SOURCE_TYPES = {
  expense: 'expense',
  purchase: 'purchase',
  payment_out: 'payment_out',
  labour: 'labour',
};

export function buildLedgerKey(sourceType, sourceId) {
  return `${String(sourceType || '').trim()}:${String(sourceId || '').trim()}`;
}

export function pickWorkOrderFields(docData = {}) {
  const workOrderId = docData.workOrderId || '';
  const phaseId = docData.phaseId || '';
  const costCategory = docData.costCategory || '';
  return { workOrderId, phaseId, costCategory };
}

export function appendWorkOrderFields(target, { workOrderId, phaseId, costCategory } = {}) {
  if (!ENABLE_WORK_ORDERS) return target;
  if (workOrderId) target.workOrderId = workOrderId;
  if (phaseId) target.phaseId = phaseId;
  if (costCategory) target.costCategory = costCategory;
  return target;
}

export function isPaymentOutflow(payment = {}) {
  const type = String(payment?.type || '').toLowerCase();
  if (type === 'payments' || type === 'purchase') return true;
  if (String(payment?.transactionType || '').toLowerCase() === 'outflow') return true;
  return false;
}

export function resolveSourceAmount(docData = {}, sourceType) {
  if (sourceType === LEDGER_SOURCE_TYPES.payment_out) {
    return parseFloat(docData.totalAmount ?? docData.amount ?? 0) || 0;
  }
  return parseFloat(docData.amount ?? docData.totalAmount ?? 0) || 0;
}

export function resolveSourceDate(docData = {}) {
  const raw =
    docData.date ||
    docData.paymentDate ||
    docData.billDate ||
    docData.invoiceDate ||
    docData.createdAt;
  if (!raw) return new Date().toISOString().split('T')[0];
  if (typeof raw === 'string') return raw.split('T')[0];
  if (raw?.toDate) return raw.toDate().toISOString().split('T')[0];
  if (raw instanceof Date) return raw.toISOString().split('T')[0];
  return new Date().toISOString().split('T')[0];
}

export async function validatePhaseBelongsToWorkOrder(db, companyCtx, workOrderId, phaseId) {
  if (!ENABLE_WORK_ORDERS) return { valid: true };
  if (!phaseId) return { valid: true };
  if (!workOrderId) {
    return { valid: false, error: 'Work order is required when a phase is selected.' };
  }
  const { companyId } = companyCtx || {};
  if (!db || !companyId) {
    return { valid: false, error: 'Missing database context.' };
  }
  const phaseRef = doc(db, 'companies', companyId, 'workOrders', workOrderId, 'phases', phaseId);
  const snap = await getDoc(phaseRef);
  if (!snap.exists()) {
    return { valid: false, error: 'Selected phase does not belong to this work order.' };
  }
  return { valid: true };
}

async function findLedgerDocByKey(db, companyCtx, ledgerKey) {
  const colRef = getCollectionForCompany(db, companyCtx, 'workOrderCostLedger');
  if (!colRef) return null;
  const q = query(colRef, where('ledgerKey', '==', ledgerKey));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, data: snap.docs[0].data() };
}

export async function upsertLedgerEntry(db, companyCtx, entry) {
  if (!ENABLE_WORK_ORDERS) return { success: true, skipped: true, disabled: true };
  const {
    workOrderId,
    phaseId = '',
    sourceType,
    sourceCollection,
    sourceId,
    amount,
    date,
    category = '',
    notes = '',
    status = 'active',
  } = entry || {};

  if (!workOrderId || !sourceType || !sourceId || !sourceCollection) {
    return { success: false, error: 'Missing required ledger fields.' };
  }

  const phaseCheck = await validatePhaseBelongsToWorkOrder(db, companyCtx, workOrderId, phaseId);
  if (!phaseCheck.valid) return { success: false, error: phaseCheck.error };

  const colRef = getCollectionForCompany(db, companyCtx, 'workOrderCostLedger');
  if (!colRef) return { success: false, error: 'Ledger collection unavailable.' };

  const ledgerKey = buildLedgerKey(sourceType, sourceId);
  const existing = await findLedgerDocByKey(db, companyCtx, ledgerKey);
  const payload = {
    workOrderId,
    phaseId: phaseId || '',
    sourceType,
    sourceCollection,
    sourceId,
    ledgerKey,
    amount: parseFloat(amount) || 0,
    date: date || new Date().toISOString().split('T')[0],
    category: category || '',
    notes: notes || '',
    status,
    updatedAt: serverTimestamp(),
  };

  if (existing) {
    const ref = doc(colRef, existing.id);
    await updateDoc(ref, payload);
    return { success: true, id: existing.id, action: 'updated' };
  }

  const ref = await addDoc(colRef, { ...payload, createdAt: serverTimestamp() });
  return { success: true, id: ref.id, action: 'created' };
}

export async function removeLedgerEntry(db, companyCtx, { sourceType, sourceId }) {
  if (!ENABLE_WORK_ORDERS) return { success: true, skipped: true, disabled: true };
  if (!sourceType || !sourceId) return { success: true, skipped: true };
  const colRef = getCollectionForCompany(db, companyCtx, 'workOrderCostLedger');
  if (!colRef) return { success: false, error: 'Ledger collection unavailable.' };

  const ledgerKey = buildLedgerKey(sourceType, sourceId);
  const existing = await findLedgerDocByKey(db, companyCtx, ledgerKey);
  if (!existing) return { success: true, skipped: true };

  await updateDoc(doc(colRef, existing.id), {
    status: 'void',
    updatedAt: serverTimestamp(),
  });
  return { success: true, id: existing.id, action: 'voided' };
}

/**
 * Sync ledger from a source document. Removes ledger when workOrderId is absent.
 */
export async function syncLedgerFromSource(db, companyCtx, { sourceType, sourceCollection, sourceId, doc: sourceDoc }) {
  if (!ENABLE_WORK_ORDERS) return { success: true, skipped: true, disabled: true };
  if (!sourceId || !sourceType || !sourceCollection) {
    return { success: false, error: 'Invalid source reference.' };
  }

  const { workOrderId, phaseId, costCategory } = pickWorkOrderFields(sourceDoc || {});

  if (!workOrderId) {
    await removeLedgerEntry(db, companyCtx, { sourceType, sourceId });
    return { success: true, action: 'removed' };
  }

  if (sourceType === LEDGER_SOURCE_TYPES.payment_out && !isPaymentOutflow(sourceDoc)) {
    await removeLedgerEntry(db, companyCtx, { sourceType, sourceId });
    return { success: true, action: 'skipped_inflow' };
  }

  const amount = resolveSourceAmount(sourceDoc, sourceType);
  if (amount <= 0) {
    await removeLedgerEntry(db, companyCtx, { sourceType, sourceId });
    return { success: true, action: 'removed_zero_amount' };
  }

  return upsertLedgerEntry(db, companyCtx, {
    workOrderId,
    phaseId,
    sourceType,
    sourceCollection,
    sourceId,
    amount,
    date: resolveSourceDate(sourceDoc),
    category: costCategory || sourceDoc?.head || sourceDoc?.category || '',
    notes: sourceDoc?.description || sourceDoc?.notes || sourceDoc?.remark || '',
  });
}

export async function generateWorkOrderNumber(db, companyCtx) {
  if (!ENABLE_WORK_ORDERS) return null;
  const colRef = getCollectionForCompany(db, companyCtx, 'workOrders');
  if (!colRef) return `WO-${Date.now()}`;
  const year = new Date().getFullYear();
  const fy = `${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
  let maxSeq = 0;
  const snap = await getDocs(colRef);
  const pattern = new RegExp(`^WO-${fy.replace('-', '\\-')}/(\\d+)$`);
  snap.docs.forEach((d) => {
    const no = String(d.data()?.workOrderNo || '');
    const m = no.match(pattern);
    if (m) maxSeq = Math.max(maxSeq, parseInt(m[1], 10));
  });
  return `WO-${fy}/${String(maxSeq + 1).padStart(3, '0')}`;
}

export async function appendLinkedDocument(db, companyCtx, workOrderId, linkedDoc) {
  if (!ENABLE_WORK_ORDERS) return;
  if (!workOrderId || !linkedDoc?.docId) return;
  const woRef = doc(db, 'companies', companyCtx.companyId, 'workOrders', workOrderId);
  const snap = await getDoc(woRef);
  if (!snap.exists()) return;
  const existing = Array.isArray(snap.data()?.linkedDocuments) ? snap.data().linkedDocuments : [];
  const dup = existing.some(
    (e) => e.docId === linkedDoc.docId && e.collection === linkedDoc.collection
  );
  if (dup) return;
  await updateDoc(woRef, {
    linkedDocuments: arrayUnion(linkedDoc),
    updatedAt: serverTimestamp(),
  });
}
